import React, { ChangeEvent, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { sanitizeAccountNumber } from '@eika-infrastruktur/format';
import { LinkButton, SmallButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { ConfirmationDialog } from '@eika/confirmation-dialog';
import { IconTypes } from '@eika/icon';

import { useMutateReceivers } from '../../fetchHooks/useReceivers';
import type { Receiver } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';
import SigningContextFlow from '../../signingContext/SigningContextFlow';
import { useSigningContext } from '../../signingContext/signingContext';
import { StickyFooterRow } from '../shared/StickyFooter';
import AccountNameField from '../transaction/Payment/SelectReceiver/AccountNameField';
import AccountNumberField from '../transaction/Payment/SelectReceiver/AccountNumberField';
import ResultDialog from './ResultDialog';
import createUpdateReceiverRequest from './form/createUpdateReceiverRequest';
import receiverDetailsReducer from './form/reducer';
import { receiverDetailsInitialState } from './form/state';
import useFormContext, { ReceiverDetailsFormContext } from './form/useFormContext';
import useHasChanged from './form/useHasChanged';
import useSetInitialReceiverState from './form/useSetInitialReceiverState';
import useValidate from './form/validate';
import useDuplicateAccountWarning from './useDuplicateAccountWarning';

import './ReceiverDetails.scss';

export interface Props {
    receivers?: Receiver[];
    receiver: Receiver;
    setDone: () => void;
    onClose: () => void;
}

const ReceiverDetails: React.FC<Props> = ({ receivers = [], receiver, onClose, setDone }) => {
    const translated = useTranslation();
    const { state, checkFormValidity, handleChange, errors } = useFormContext();
    const { action, accountNumber, accountName } = state;
    useSetInitialReceiverState(receiver);

    const { remove: removeReceiver } = useMutateReceivers();
    const { signingStatus, signingOperationPending, startSigning } = useSigningContext(createUpdateReceiverRequest);

    const [showDeleteDialogue, setShowDeleteDialogue] = useState(false);

    const accountNumberSanitized = useMemo(() => sanitizeAccountNumber(accountNumber), [accountNumber]);

    const accountNumberList = useMemo(
        () => receivers?.map((account) => account.accountNumber.value) ?? [],
        [receivers],
    );

    const handleUpdateState = (stateKey: 'account-number' | 'account-name') => (e: ChangeEvent<HTMLInputElement>) =>
        handleChange({
            type: stateKey,
            payload: e.target.value,
        });

    const duplicateAccountWarning = useDuplicateAccountWarning(
        accountNumberList,
        accountNumberSanitized,
        receiver,
        receivers,
    );
    const hasChanged = useHasChanged(receiver, accountNumberSanitized);

    const changeReceiver = useCallback(
        async (e: React.FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            if ((await checkFormValidity()) && !duplicateAccountWarning && hasChanged) {
                handleChange({ type: 'action', payload: 'edit' });
                startSigning({ ...state, accountNumber: accountNumberSanitized, receiverId: receiver.id });
            }
        },
        [
            checkFormValidity,
            duplicateAccountWarning,
            hasChanged,
            startSigning,
            accountNumberSanitized,
            receiver,
            handleChange,
            state,
        ],
    );

    const handleDelete = () => {
        handleChange({ type: 'action', payload: 'delete' });
        removeReceiver.mutateAsync(receiver.id).then(() => setDone());
    };

    const closeDialogue = useCallback(() => {
        onClose();
        removeReceiver.reset();
    }, [onClose, removeReceiver]);

    const updateDone = action === 'edit' && signingStatus !== undefined && signingStatus !== 'CREATED';
    const deleteDone = action === 'delete' && removeReceiver.isSuccess;

    useEffect(() => {
        if (updateDone) {
            if (signingStatus === 'COMPLETED') {
                setDone();
            }
        }
    }, [action, signingStatus, setDone, updateDone]);

    if (deleteDone || updateDone) {
        return (
            <ResultDialog
                retryChange={changeReceiver}
                retryDelete={handleDelete}
                action={action}
                deleteError={removeReceiver.error}
            />
        );
    }

    const loading = signingOperationPending;

    return (
        <SigningContextFlow onClosePayment={closeDialogue}>
            <form onSubmit={changeReceiver}>
                <h2>{translated.details}</h2>
                <AccountNameField
                    accountName={accountName}
                    handleAccountNameChange={handleUpdateState('account-name')}
                    translated={translated}
                    validationMessage={errors.accountName}
                />
                <AccountNumberField
                    accountNumber={accountNumber}
                    handleAccountNumberChange={handleUpdateState('account-number')}
                    translated={translated}
                    validationMessage={errors.accountNumber ?? duplicateAccountWarning}
                    attentionMessage={duplicateAccountWarning}
                />
                <div className="receiver-details__delete-btn">
                    {!showDeleteDialogue && (
                        <SmallButton
                            buttonType="negative"
                            onClick={() => setShowDeleteDialogue(true)}
                            icon={IconTypes.SLETT_24}
                        >
                            {translated.delete}
                        </SmallButton>
                    )}

                    <ConfirmationDialog
                        title={translated.confirmDeleteReceiver}
                        id="delete-receiver-confirmation"
                        footer={
                            <>
                                <LinkButton
                                    onClick={() => setShowDeleteDialogue(false)}
                                    disabled={removeReceiver.isPending}
                                >
                                    {translated.cancel}
                                </LinkButton>
                                <ButtonWithSpinner
                                    buttonType="positive"
                                    size="small"
                                    onClick={handleDelete}
                                    showSpinner={removeReceiver.isPending}
                                >
                                    {translated.confirmDelete}
                                </ButtonWithSpinner>
                            </>
                        }
                        show={showDeleteDialogue}
                        onExitFocus={() => setShowDeleteDialogue(false)}
                        blueBackground
                    />
                </div>
                {hasChanged && (
                    <StickyFooterRow>
                        <LinkButton onClick={onClose} disabled={loading}>
                            {translated.cancel}
                        </LinkButton>
                        <ButtonWithSpinner
                            buttonType="positive"
                            size="small"
                            type="submit"
                            showSpinner={loading}
                            onClick={changeReceiver}
                        >
                            {translated.save}
                        </ButtonWithSpinner>
                    </StickyFooterRow>
                )}
            </form>
        </SigningContextFlow>
    );
};
export default (props: Props) => (
    <ReceiverDetailsFormContext
        validateHook={useValidate}
        reducer={receiverDetailsReducer}
        initialState={receiverDetailsInitialState}
    >
        <ReceiverDetails {...props} />
    </ReceiverDetailsFormContext>
);
